import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "@ca-dmv-radv/translation";
import { Button, ButtonWrapper } from "@ca-dmv/core";
import { RadvPageWrapper, ButtonSaveAndExit } from "@ca-dmv-radv/components";
import { getPaymentDetails, postPayment } from "@ca-dmv-radv/data";

import {
  PAYMENT_STATUS_COMPLETED,
  PAYMENT_STATUS_INITIATED,
} from "../constants";

const APPLICATION_TYPE = "nch";

export default function NCHPaymentLanding({ title }) {
  const { t } = useTranslation();
  const formProps = { isForm: false };
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [feeAmount, setFeeAmount] = useState(0);
  const [skipDMV, setSkipDMV] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await getPaymentDetails(APPLICATION_TYPE);

      if (response && response.success && response.data) {
        const {
          skipDMV: skip,
          paymentStatus: status,
          feeAmount: fee,
        } = response.data;

        setSkipDMV(skip || false);
        setPaymentStatus(status || null);
        setFeeAmount(fee != null ? fee : 0);
      }
    } catch {
      setSkipDMV(false);
      setPaymentStatus(null);
      setFeeAmount(0);
      return undefined;
    }
    return undefined;
  }, []);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  useEffect(() => {
    if (paymentStatus?.toUpperCase() === PAYMENT_STATUS_INITIATED) {
      const intervalId = setInterval(fetchPaymentDetails, 10000);
      return () => clearInterval(intervalId);
    }
    return undefined;
  }, [paymentStatus, fetchPaymentDetails]);

  const goToApplication = useCallback(() => {
    window.location.assign("/nch");
  }, []);

  const initiatePaymentWorkflow = useCallback(async () => {
    setIsLoading(true);
    try {
      const { success, data } = await postPayment(APPLICATION_TYPE);
      if (!success || !data) throw new Error("Payment initiation failed");
      window.location.assign(data);
    } catch (error) {
      setIsLoading(true);
      window.location.assign("/nch");
    }
  }, []);

  // Determine whether to hide Pay Now
  const hidePayNow =
    !skipDMV || // skipDMV false → hide button
    paymentStatus?.toUpperCase() === PAYMENT_STATUS_COMPLETED || // payment already initiated → hide
    feeAmount <= 0; // feeAmount zero → hide

  const pageHeading = () => {
    return t("document-upload-checlist", "Document Upload Checklist");
  };

  return (
    <RadvPageWrapper
      fullWidth
      formProps={formProps}
      showRequiredFieldSeparator={false}
      pageTitle={title}
      pageHeading={pageHeading()}
    >
      <div id="nch-landing-div" className="container container--full">
        <ol className="nch-landing-large-list-block__list ">
          <li className="nch-landing-large-list-block__list-item">
            <span className="list-text">
              {t(
                "nch-landing-gather-the-required-proof-documents-to-show-dmv",
                "Gather the required proof documents to show DMV."
              )}
              <a
                className="pdf-link"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.dmv.ca.gov/portal/file/federal-non-compliant-dl-id-card-documents-list-pdf/"
              >
                {" "}
                {t(
                  "nch-landing-see-acceptable-list-of-documents-pdf",
                  "See acceptable documents list (PDF)"
                )}
              </a>
            </span>
          </li>
          {!hidePayNow && (
            <li className="nch-landing-large-list-block__list-item">
              {t(
                "nch-landing-select-the-paynow-button-to-continue-with-your-application",
                "Select the 'Pay Now' button to continue with your application."
              )}
            </li>
          )}
        </ol>
        <ButtonWrapper wrapperClass="nch-landing-flex--col-reverse mt-60 bp-md:mt-60">
          <ButtonSaveAndExit
            buttonClass="bp-md:mr-24 mb-24 bp-md:mb-0"
            ignoreSendEmail
          />

          {hidePayNow ? (
            <Button
              label={t("nch-continue", "Continue")}
              onClick={goToApplication}
            />
          ) : (
            <Button
              label={t("nch.payNow", "Pay Now")}
              isLoading={isLoading}
              disabled={isLoading}
              onClick={initiatePaymentWorkflow}
            />
          )}
        </ButtonWrapper>
      </div>
    </RadvPageWrapper>
  );
}
